import { useEffect, useState, FC, Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "components/Buttons/ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import StayDatesRangeInput from "./StayDatesRangeInput";
import moment from "moment";

export interface StayDatesRangeInputWrapperProps {
  className?: string;
  startDateParam?: Date;
  endDateParam?: Date;
  onChange: (dates: [Date | null, Date | null]) => void;
  onMonthChange?: (date: Date) => void;
  show?: boolean;
  setShow?: (value: boolean) => void;
  rangeAvailability?: any;
  freeSelection?: boolean;
}

const StayDatesRangeInputWrapper: FC<StayDatesRangeInputWrapperProps> = ({
  className = "flex-1",
  startDateParam,
  endDateParam,
  onChange,
  onMonthChange,
  show,
  setShow,
  rangeAvailability,
  freeSelection,
}) => {
  // Internal state for the component
  const [internalStartDate, setInternalStartDate] = useState<Date>(startDateParam || new Date());
  const [internalEndDate, setInternalEndDate] = useState<Date>(endDateParam || new Date());

  // Sync internal state with props when they change
  useEffect(() => {
    if (startDateParam) {
      setInternalStartDate(startDateParam);
    }
    if (endDateParam) {
      setInternalEndDate(endDateParam);
    }
  }, [startDateParam, endDateParam]);

  // Handle date changes from the simple component
  const handleInternalDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setInternalStartDate(start);
    setInternalEndDate(end);
    
    // Notify parent component
    onChange([start, end]);
  };

  // We need to modify the StayDatesRangeInput to accept initial dates
  // Since it doesn't support props for initial dates, we'll create a modified version
  return (
    <div className={className}>
      <StayDatesRangeInputWithInitialDates
        startDate={internalStartDate}
        endDate={internalEndDate}
        onChange={handleInternalDateChange}
        className={className}
        freeSelection={freeSelection}
      />
    </div>
  );
};

// A modified version of StayDatesRangeInput that accepts initial dates
const StayDatesRangeInputWithInitialDates: FC<{
  startDate?: Date;
  endDate?: Date;
  onChange: (dates: [Date, Date]) => void;
  className?: string;
  freeSelection?: boolean;
}> = ({ startDate, endDate, onChange, className, freeSelection }) => {
  const [currentStartDate, setCurrentStartDate] = useState<Date>(startDate || new Date());
  const [currentEndDate, setCurrentEndDate] = useState<Date>(endDate || new Date());

  // Update internal state when props change.
  // In free-selection mode non re-iniettiamo la data di fine dai props:
  // altrimenti il range tornerebbe "completo" e react-datepicker, al secondo
  // click, ricomincerebbe la selezione invece di chiudere il range.
  useEffect(() => {
    if (startDate) {
      setCurrentStartDate(startDate);
    }
    if (endDate && !freeSelection) {
      setCurrentEndDate(endDate);
    }
  }, [startDate, endDate, freeSelection]);

  const onChangeDate = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setCurrentStartDate(start);
    setCurrentEndDate(end);
    onChange([start, end]);
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span className="block xl:text-lg font-semibold">
            {currentStartDate?.toLocaleDateString("it-IT", {
              month: "short",
              day: "2-digit",
            }) || "Add dates"}
            {currentEndDate
              ? " - " +
              currentEndDate?.toLocaleDateString("it-IT", {
                month: "short",
                day: "2-digit",
              })
              : ""}
          </span>
          <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
            {"Check in - Check out"}
          </span>
        </div>
      </>
    );
  };

  return (
    <Popover className={`StayDatesRangeInput z-10 relative flex ${className}`}>
      {({ open }) => (
        <>
          <Popover.Button
            className={`flex-1 flex relative p-3 items-center space-x-3 focus:outline-none ${
              open ? "shadow-lg" : ""
            }`}
          >
            {renderInput()}
            {currentStartDate && open && (
              <ClearDataButton onClick={() => onChangeDate([null, null])} />
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute left-auto xl:-right-10 right-0 z-10 mt-3 top-full w-screen max-w-sm px-4 sm:px-0 lg:max-w-3xl">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
                <DatePicker
                  selected={currentStartDate}
                  onChange={onChangeDate}
                  startDate={currentStartDate}
                  endDate={currentEndDate}
                  selectsRange
                  monthsShown={2}
                  showPopperArrow={false}
                  inline
                  renderCustomHeader={(p) => (
                    <DatePickerCustomHeaderTwoMonth {...p} />
                  )}
                  renderDayContents={(day, date) => (
                    <DatePickerCustomDay dayOfMonth={day} date={date} />
                  )}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default StayDatesRangeInputWrapper;
